import { useState, useEffect } from "react";
import Popup from "../Popup";
import FormHeader from "../form/Header";
import { useTranslation } from "react-i18next";
import FormRepeater from "../form/FormRepeater";
import MessageError from "../MessageError";
import { Link } from "react-router-dom";
import CustomTooltip from "../CustomTooltip";
import { DeleteIcon, LoadingIcon, PrintIcon } from "../../assets/icons/Icons";
import { replaceCard } from "../../api/sell-orders/replaceCard";
import { ValidateCard } from "../../api/sell-orders/validateCard";
import OfficesSelect from "../form/OfficesSelect";
import FormSection from "../form/Section";
import Message from "../Message";
import { CheckCardBalance } from "../../api/sell-orders/checkCardBalance";
import usePrint from "../../hooks/usePrint";
import { getReplaceCardReceipt } from "../../api/sell-orders/getReplaceCardReceipt";
import SkipReplacementFee from "../SkipReplacementFee";
import { GetProgramFees } from "../../api";
import { CentsToDollars } from "../../utils/formatPrice";
import { getCardByPrn } from "../../api/sell-orders/getCardByPrn";

const ReplaceCardModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [officeId, setOfficeId] = useState(null);
  const [cardsToReplace, setCardsToReplace] = useState([]);
  const [cardsToReplaceWith, setCardsToReplaceWith] = useState([]);
  const [cardsReplaced, setCardsReplaced] = useState(null);
  const [disabledReplaceButton, setDisabledReplaceButton] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [skipReplacementFee, setSkipReplacementFee] = useState(false);
  const [replacementFee, setReplacementFee] = useState(null);
  const [program_id, setProgramId] = useState(null);

  const oldCardBalance = cardsToReplace[0]?.balance
    ? Number(cardsToReplace[0].balance)
    : null;

  const effectiveFee =
    skipReplacementFee ? 0 : Number(replacementFee ?? 0);

  const netBalance =
    oldCardBalance !== null ? (oldCardBalance * 100) - effectiveFee : null;

  const handleReplaceCard = async () => {
    setError("");
    setLoading(true);
    if (cardsToReplace && cardsToReplaceWith && officeId) {
      try {
        const { data: id } = await replaceCard({
          office_id: officeId,
          old_processor_reference: cardsToReplace[0].processor_reference,
          new_processor_reference: cardsToReplaceWith[0].processor_reference,
          skip_replace_fee: skipReplacementFee,
        });
        setCardsReplaced(id);
        setCardsToReplaceWith([]);
        setCardsToReplace([]);
      } catch (err) {
        const msg = err.message || "";
        if (
          msg.toLowerCase().includes("insufficient") ||
          msg.toLowerCase().includes("balance")
        ) {
          const feeNote = ` (${t("Replacement Fee")}: $${CentsToDollars(replacementFee)})`;
          setError(
            msg +
              feeNote +
              ". " +
              t("You may skip the fee using the checkbox above.")
          );
        } else {
          setError(msg);
        }
        console.log("Error fetching data: " + err);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (
      cardsToReplace[0]?.card_type &&
      cardsToReplace[0]?.processor_reference &&
      cardsToReplaceWith[0]?.card_type &&
      cardsToReplaceWith[0]?.processor_reference
    ) {
      setDisabledReplaceButton(false);
    } else {
      setDisabledReplaceButton(true);
    }
  }, [cardsToReplace, cardsToReplaceWith]);

  useEffect(() => {
    async function getProgramFees() {
      try {
        const programFeeRes = await GetProgramFees({
          program_id: program_id,
        });
        setReplacementFee(
          programFeeRes?.data?.sell_order_replace_card_fee ?? null
        );
      } catch (error) {
        console.log(error);
      }
    }
    if (program_id) {
      getProgramFees();
    }
  }, [program_id]);

  const { handlePrint } = usePrint(getReplaceCardReceipt);

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <FormHeader title={t("Replace Cards")}></FormHeader>

      <div className="row mb-3 mt-4">
        <div className="col-xs-12 col-md-6">
          <FormSection title={t("Select an Office")} required>
            <OfficesSelect
              onChange={setOfficeId}
              defaultOfficeIds={[officeId]}
            />
          </FormSection>
        </div>
      </div>

      <div className="mb-3">
        <h5 className="mb-0">{t("Old Card Details")}</h5>
        <hr className="size-3" />

        <FormRepeater
          columns={[
            t("Proxy Number"),
            "",
            t("Product Description"),
            t("Balance"),
            t("Clear"),
          ]}
          maxRows={1}
          addRowText={t("Add Card")}
          RenderComponent={CardRow}
          onRowChange={setCardsToReplace}
          rowData={cardsToReplace}
          officeId={officeId}
          skipValidation={true}
          setProgramId={setProgramId}
        />
      </div>

      <div className="mb-3">
        <h5 className="mb-0">{t("New Card Details")}</h5>
        <hr className="size-3" />

        <FormRepeater
          columns={[
            t("Proxy Number"),
            "",
            t("Product Description"),
            t("Balance"),
            t("Clear"),
          ]}
          maxRows={1}
          addRowText={t("Add Card")}
          RenderComponent={CardRow}
          onRowChange={setCardsToReplaceWith}
          rowData={cardsToReplaceWith}
          officeId={officeId}
        />
      </div>

      <div className="d-block mb-2">
        <SkipReplacementFee
          initialValue={skipReplacementFee}
          onChange={(e) =>
            setSkipReplacementFee(e.target.checked ? true : false)
          }
        />
        <p className="mb-1">
          {t("Replacement Fee")}:{" "}
          <b>
            ${CentsToDollars(replacementFee)}
          </b>
        </p>
        {netBalance !== null && (
          <p className="mb-3">
            {t("Net Balance After Fee")}:{" "}
            <b>${CentsToDollars(netBalance)}</b>
          </p>
        )}
      </div>

      <MessageError error={error} className="text-center" />

      {cardsReplaced && (
        <>
          <Message
            message={t("Card successfully replaced!")}
            type="message-success"
            className="mb-2"
          />
          <button
            className="btn btn-medium btn-secondary"
            onClick={(e) => {
              e.preventDefault();
              handlePrint({
                requestParams: { id: cardsReplaced.id },
                name: `order-${cardsReplaced.id}`,
                width: "",
                height: "",
                download: false,
              });
            }}
          >
            <PrintIcon />
            {t("Print Receipt")}
          </button>
        </>
      )}

      {!cardsReplaced && (
        <button
          className={`btn btn-primary btn-medium ${
            loading ? "btn-loading" : ""
          }`}
          disabled={disabledReplaceButton || loading}
          onClick={handleReplaceCard}
        >
          {loading && <LoadingIcon />} {t("Replace Cards")}
        </button>
      )}
    </Popup>
  );
};

export default ReplaceCardModal;

const CardRow = ({ rowData, rowIndex, onFieldChange, removeRow, ...props }) => {
  const { t } = useTranslation();
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [validationLoading, setValidationLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckButton = async () => {
    setValidationLoading(true);

    if (props.skipValidation) {
      try {
        const [balanceRes, cardRes] = await Promise.all([
          CheckCardBalance({ prn: rowData.processor_reference }),
          getCardByPrn({ prn: rowData.processor_reference }),
        ]);
        props.setProgramId &&
          props.setProgramId(cardRes?.data?.accounts?.[0].program_id ?? null);
        onFieldChange({
          card_type: "Oxford Gift Card PLUS",
          balance: balanceRes.data.balance,
        });
        setError("");
        setValidationLoading(false);
      } catch (err) {
        console.log("Error fetching data: " + err);
        setValidationLoading(false);
      }
      return;
    }

    setError("");
    onFieldChange("card_type", "");
    try {
      const response = await ValidateCard({
        processor_reference: rowData.processor_reference,
        office_id: props.officeId,
      });
      if (response?.error?.message) {
        setError(response.error.message);
      } else {
        try {
          const {
            data: { balance },
          } = await CheckCardBalance({ prn: rowData.processor_reference });
          onFieldChange({
            card_type: "Oxford Gift Card PLUS",
            balance: balance,
          });
          setError("");
        } catch (err) {
          console.log("Error fetching data: " + err);
        }
      }
    } catch (err) {
      console.log("Error fetching data: " + err);
    }

    setValidationLoading(false);
  };

  useEffect(() => {
    onFieldChange("card_type", "");
    if (rowData.processor_reference?.length === 12 && props.officeId) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
    // eslint-disable-next-line
  }, [rowData.processor_reference]);

  return (
    <>
      <td>
        <input
          type="number"
          placeholder={t("Proxy Number")}
          value={rowData.processor_reference || ""}
          onChange={(e) => onFieldChange("processor_reference", e.target.value)}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown")
              e.preventDefault();
          }}
        />
        <MessageError error={error} />
      </td>
      <td>
        <button
          className={`btn btn-primary btn-small ${
            validationLoading ? "btn-loading" : ""
          }`}
          onClick={handleCheckButton}
          disabled={buttonDisabled}
        >
          {validationLoading ? <LoadingIcon /> : ""}
          {t("Validate")}
        </button>
      </td>
      <td>
        <input
          type="text"
          placeholder={t("Card Type")}
          value={rowData.card_type || ""}
          onChange={(e) => onFieldChange("card_type", e.target.value)}
          disabled
        />
      </td>
      <td>
        <input
          type="text"
          placeholder={t("Balance")}
          value={rowData.balance || ""}
          onChange={(e) => onFieldChange("balance", e.target.value)}
          disabled
        />
      </td>
      <td className="text-center">
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            removeRow(rowIndex);
          }}
        >
          <CustomTooltip icon={<DeleteIcon />} message={t("Remove Row")} />
        </Link>
      </td>
    </>
  );
};
